import { useState, useEffect } from 'react';
import Input from '../../components/form/input/InputField';
import Label from '../../components/form/Label';
import { Link } from 'react-router';
import { EyeCloseIcon, EyeIcon } from '../../icons';
import PageMeta from '../../components/common/PageMeta';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { usePermissionStore } from '../../store/usePermissionStore';

export default function SignIn() {
  console.log('[SignIn] ========== Component rendered ==========');
  
  useEffect(() => {
    console.log('[SignIn] ========== Component mounted ==========');
    return () => {
      console.log('[SignIn] ========== Component unmounted ==========');
    };
  }, []);
  
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const signIn = useAuthStore((state) => state.signIn);
  const navigate = useNavigate();
  
  console.log('[SignIn] Hooks initialized, navigate:', typeof navigate, 'signIn:', typeof signIn);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[SignIn] Form submitted with email:', email);
    
    try {
      const success = await signIn(email, password);
      console.log('[SignIn] signIn result:', success);
      
      if (success) {
        console.log('[SignIn] Sign-in successful, navigating to /families');
        // Always navigate to families after sign-in, regardless of permissions
        // The page will show AccessDenied if user doesn't have permission
        navigate('/families', { replace: true });
        console.log('[SignIn] Navigation called');
      } else {
        console.log('[SignIn] Sign-in failed - success is false');
      }
    } catch (error) {
      console.error('[SignIn] Error during sign-in:', error);
    }
  };

  return (
    <>
      <PageMeta title="Sign In | St Antony's Church" description="Sign in to your account at St Antony's Church" />
      <div className="relative flex w-full h-screen overflow-hidden bg-white z-1 sm:p-0">
        {/* Left Side - Sign In Form */}
        <div className="flex flex-col flex-1 p-6 sm:p-8 lg:p-12">
          <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
            {/* Logo/Brand */}
            <div className="mb-8">
              <Link to="/" className="inline-block mb-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#B8860B' }}>
                  St Antony's Church
                </h1>
              </Link>
            </div>

            <div>
              <div className="mb-8">
                <h1 className="mb-2 font-bold text-gray-800 text-3xl sm:text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Sign In
                </h1>
                <p className="text-sm text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Enter your email and password to sign in!
                </p>
              </div>

              <div>
                <form 
                  onSubmit={(e) => {
                    console.log('[SignIn] Form onSubmit triggered');
                    handleSubmit(e);
                  }}
                >
                  <div className="space-y-6">
                    <div>
                      <Label>
                        Email <span className="text-error-500">*</span>{' '}
                      </Label>
                      <Input placeholder="info@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>
                    <div>
                      <Label>
                        Password <span className="text-error-500">*</span>{' '}
                      </Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <span
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2"
                        >
                          {showPassword ? <EyeIcon className="fill-gray-500" /> : <EyeCloseIcon className="fill-gray-500" />}
                        </span>
                      </div>
                    </div>

                    <div>
                      <button
                        type="submit"
                        onClick={() => console.log('[SignIn] Button clicked')}
                        className="w-full px-6 py-3 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-semibold shadow-lg"
                        style={{ fontFamily: 'Lato, sans-serif' }}
                      >
                        Sign In
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/"
                  className="text-sm text-gray-500 hover:text-[#B8860B] transition-colors inline-flex items-center gap-2"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Welcome Message */}
        <div className="relative items-center justify-center flex-1 hidden p-8 z-1 lg:flex bg-gradient-to-br from-[#B8860B] via-[#9A7209] to-[#7C5D07]">
          <div className="relative z-10 flex flex-col items-center max-w-md text-center">
            <div className="mb-6">
              <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Welcome to St Antony's Church
              </h2>
              <p className="text-lg text-white/90 mb-2" style={{ fontFamily: 'Lato, sans-serif' }}>
                A Community of Faith, Hope, and Love
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
